import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PATH_TRANSLATIONS, resolveSupportedLang, SUPPORTED_LANGS, SupportedLang } from '../../../shared/config/localized-paths';
import { NavItem } from '../../models/nav-item.model';
import { NavDropdownComponent } from '../nav-dropdown/nav-dropdown.component';
import { NavigationService } from '../../services/navigation.service';
import { LanguageService } from '../../../shared/services/language.service';

/**
 * Row 2 — desktop primary navigation bar.
 * Renders the primary nav items and manages which dropdown (if any) is open.
 * Hidden on mobile (< LG); the mobile drawer renders its own nav.
 */
@Component({
  selector: 'xbh-header-nav',
  templateUrl: './header-nav.component.html',
  styleUrls: ['./header-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderNavComponent implements OnInit, AfterViewInit {
  /** Index of the currently open dropdown (-1 = none) */
  openDropdownIndex = -1;
  private currentUrl = '';
  private readonly destroyRef = inject(DestroyRef);

  /** Reference to the rendered dropdown panel (only available when a dropdown is open) */
  @ViewChild(NavDropdownComponent) private dropdownRef?: NavDropdownComponent;
  /** References to every dropdown trigger button, indexed in DOM order */
  @ViewChildren('navTrigger') private triggerRefs!: QueryList<ElementRef<HTMLButtonElement>>;

  constructor(
    private readonly hostEl: ElementRef<HTMLElement>,
    private readonly navigationService: NavigationService,
    private readonly languageService: LanguageService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
  ) {
    this.currentUrl = this.router.url;
  }

  ngAfterViewInit(): void {
    // Best-effort immediate pin so the fix applies even when document.fonts is unavailable.
    this.pinNavLinkBoldWidths();
    // Re-pin after fonts load for accurate measurement with custom font metrics.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => this.pinNavLinkBoldWidths());
    }
  }

  private pinNavLinkBoldWidths(): void {
    const links = this.hostEl.nativeElement.querySelectorAll<HTMLElement>('.myce-header-nav__link');
    links.forEach(link => {
      link.style.fontWeight = 'bold';
      const boldWidth = link.getBoundingClientRect().width;
      link.style.fontWeight = '';
      link.style.minWidth = `${boldWidth}px`;
    });
  }

  ngOnInit(): void {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(e => {
      this.currentUrl = e.urlAfterRedirects;
      this.openDropdownIndex = -1;
      this.cdr.markForCheck();
    });

    // Re-pin after language service toggles .english/.spanish span visibility.
    this.translateService.onLangChange.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      setTimeout(() => this.pinNavLinkBoldWidths());
    });
  }

  isDropdownActive(item: NavItem): boolean {
    if (!item.hasDropdown || !item.dropdownItems) { return false; }
    const activeSegment = this.getActiveRouteSegment();
    if (!activeSegment) { return false; }

    const matchesDropdownChild = item.dropdownItems.some(child => {
      if (!child.routeKey) { return false; }
      return this.getLocalizedRouteCandidates(child.routeKey).has(activeSegment);
    });

    if (matchesDropdownChild) { return true; }

    return (item.activeForRouteKeys ?? []).some(key =>
      this.getLocalizedRouteCandidates(key).has(activeSegment),
    );
  }

  private getActiveRouteSegment(): string | null {
    const tree = this.router.parseUrl(this.currentUrl || this.router.url);
    const segments = tree.root.children['primary']?.segments ?? [];
    if (!segments.length) { return null; }

    const first = segments[0].path;
    if (SUPPORTED_LANGS.includes(first as SupportedLang)) {
      return segments[1]?.path ?? null;
    }

    return first;
  }

  private getLocalizedRouteCandidates(routeKey: string): Set<string> {
    const candidates = new Set<string>([routeKey]);
    for (const lang of SUPPORTED_LANGS) {
      const localized = PATH_TRANSLATIONS[lang][routeKey];
      if (localized) {
        candidates.add(localized);
      }
    }
    return candidates;
  }

  get navItems(): NavItem[] {
    return this.navigationService.primaryNavItems;
  }

  isDropdownOpen(index: number): boolean {
    return this.openDropdownIndex === index;
  }

  openDropdown(index: number): void {
    this.openDropdownIndex = index;
    this.applyDropdownLanguageVisibility();
  }

  toggleDropdown(index: number): void {
    this.openDropdownIndex = this.openDropdownIndex === index ? -1 : index;
    if (this.openDropdownIndex >= 0) {
      this.applyDropdownLanguageVisibility();
    }
  }

  private applyDropdownLanguageVisibility(): void {
    setTimeout(() => this.languageService.applyLanguageVisibility(resolveSupportedLang(this.translateService.currentLang)));
  }

  /**
   * Closes the dropdown and returns keyboard focus to the trigger button.
   * Use for keyboard-initiated closes (Escape, ArrowUp on first item).
   */
  closeDropdown(): void {
    const prevIndex = this.openDropdownIndex;
    this.openDropdownIndex = -1;
    if (prevIndex >= 0) {
      setTimeout(() => {
        this.triggerRefs?.toArray()[prevIndex]?.nativeElement?.focus();
      }, 0);
    }
  }

  /**
   * Closes the dropdown without moving focus.
   * Use for mouse/pointer-initiated closes (mouseleave, click outside).
   */
  closeSilently(): void {
    this.openDropdownIndex = -1;
  }

  onNavKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const wasOpen = this.isDropdownOpen(index);
      this.toggleDropdown(index);
      if (!wasOpen) {
        // Dropdown just opened — move focus into first item after Angular renders it
        setTimeout(() => this.dropdownRef?.focusFirst(), 0);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isDropdownOpen(index)) {
        this.openDropdown(index);
      }
      setTimeout(() => this.dropdownRef?.focusFirst(), 0);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.openDropdownIndex < 0) { return; }

    const target = event.target as Node | null;
    if (target && !this.hostEl.nativeElement.contains(target)) {
      this.closeSilently();
    }
  }

  trackByLabel(_index: number, item: NavItem): string {
    return item.labelEn;
  }
}
